import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin } from 'rxjs';
import { Vessel, Voyage } from 'src/app/transportation/vessels/vessels.service';
import { MessageLevel, PgMessagesService } from 'src/app/shared/services/pg-messages.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { KeycloakService } from 'src/app/authentication/keycloak.service';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import moment from 'moment';
import { PgToolbarComponent } from '../../../../shared/components/title-bar/title-bar.component';
import { CrewChangeToolbarComponent } from './crew-change-toolbar-component/crew-change-toolbar.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { CrewChangePlansComponent } from './crew-change-plans/crew-change-plans.component';
import { CrewContractComponent } from './contract/crew-contract.component';
import { MaterialImageViewerComponent } from 'src/app/shared/components/material-image-viewer/material-image-viewer';
import { MaterialAutoCompleteComponent } from 'src/app/shared/components/material-autocomplete/material-auto-complete.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCard, MatCardTitle, MatCardHeader, MatCardContent } from '@angular/material/card';
import { CrewServiceSpa } from 'src/app/generated/services/transportation/crew/crewSpa';
import { VesselServiceSpa } from 'src/app/generated/services/transportation/vessels/vesselSpa';
import { VoyageServiceSpa } from 'src/app/generated/services/transportation/voyages/voyageSpa';
import { MaterialPgButtonComponent } from 'src/app/shared/components/material-pg-button/material-pg-button.component';
import { CrewChange, Crew, CrewChangeItem, CrewContract, UpdateCrewChangePlanRequest, AddNewCrewChangeRequest, UpdateCrewChangeContractRequest } from 'src/app/generated/services/transportation/crew/crew';
import { LookupServiceSpa } from 'src/app/generated/services/lookups/lookupSpa';
import { MaterialPgInputComponent } from 'src/app/shared/components/material-pg-input/material-pg-input.component';
import { MatDialog } from '@angular/material/dialog';
import { TemporaryPasswordDialogComponent } from './temporary-password-dialog.component';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-crew-change',
  templateUrl: './crew-change.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    RouterModule,
    CrewChangePlansComponent,
    CrewContractComponent,
    MaterialImageViewerComponent,
    MaterialAutoCompleteComponent,
    MaterialPgInputComponent,
    MatDividerModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatCard,
    MatCardTitle,
    MatCardHeader,
    MatCardContent,
    MaterialPgButtonComponent],
})
export class CrewChangeComponent implements OnInit, PgToolbarComponent {
  title = new EventEmitter<string>();
  crewChange!: CrewChange;
  crew: Crew = Crew.create();
  organisations: Organisation[] = [];
  planMode!: boolean;
  voyages: Voyage[] = [];
  selectedVessel!: Vessel;
  selectedVoyage!: Voyage;
  cancelMode!: boolean;
  vessels: Vessel[] = [];
  message!: string;
  crewId!: string | null;
  crewChangeId?: string;
  ranks: string[] = [];
  expectedVersion!: number;

  toolbarComponentType = CrewChangeToolbarComponent;
  toolbarComponent!: CrewChangeToolbarComponent;

  selectedCrewPreviousAssignments: CrewChange[] = [];

  constructor(
    private _keycloakService: KeycloakService,
    private _organisationService: OrganisationService,
    private _lookupServiceSpa: LookupServiceSpa,
    private _activateRoute: ActivatedRoute,
    private _router: Router,
    private _pgMessagesService: PgMessagesService,
    private _crewService: CrewServiceSpa,
    private _vesselService: VesselServiceSpa,
    private _voyageService: VoyageServiceSpa,
    private _dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.voyages = [];
    this.planMode = false;
    this._activateRoute.paramMap.subscribe((p) => {
      this.crewId = p.get('id');
      this.crewChangeId = p.get('crewChangeId') || undefined;
      forkJoin([
        this._vesselService.getActiveVessels({}),
        this._organisationService.get(),
        this._lookupServiceSpa.getLookupItems({categoryName: 'ranks'}),
      ]).subscribe((results) => {
        this.vessels = results[0].vessels;
        this.organisations = results[1];
        this.ranks = results[2].items.flatMap(i => i.values);
        this.initCrewChange();
      });
    });
  }

  rankFormatter(data: any) {
    return data;
  }

  voyageFormatter(voyage: Voyage) {
    if (voyage) {
      return `${voyage.shipVoyageNo}`;
    }
    return '';
  }

  initCrewChange() {
    this._crewService.getCrewById({id: this.crewId || ''})
      .then((response) => {
        this.title.emit('Crew change');
        this.crew = response.data || Crew.create();
        this.expectedVersion = response.version;
        if (this.crewChangeId) {
          this.cancelMode = false;
          this.crewChange = this.crew.crewChanges.find(cr => cr.id === this.crewChangeId) || CrewChange.create();
          this.selectedVessel = this.vessels.find((v) => v.imo === this.crewChange.vesselImo) || Vessel.create();
          this.selectVessel(this.selectedVessel);
          this.toolbarComponent.crewChange = this.crewChange;
          this.toolbarComponent.crew = this.crew;
          this.toolbarComponent.cancelPlan.subscribe(() => this.cancelAll());
          this.toolbarComponent.deleteCrewChange.subscribe(() => this.remove());
          this.toolbarComponent.newContractExtension.subscribe(() => this.newContractExtension());
        } else {
          this.crewChange = CrewChange.create();
          this.cancelMode = false;
        }
      });
  }

  selectVessel(v: Vessel) {
    if (v) {
      this.crewChange.vesselImo = v.imo;
      this.crewChange.vesselName = v.name!.name;
      this._voyageService.getVoyagesByImo({vesselImo: v.imo}).then((response) => {
        this.voyages = response.voyages.sort((v1, v2) => v2.shipVoyageNo.localeCompare(v1.shipVoyageNo));
        if (this.crewChangeId) {
          this.selectedVoyage = response.voyages.find(voyage => voyage.shipVoyageNo === this.crewChange.voyageNumber) || Voyage.create();
        }
      });
    }
  }

  cancelAll() {
    if (confirm('Are you sure you want to Cancel this CrewChange?')) {
      this.crewChange.isCancelled = true;
      this.save();
    }
  }

  remove() {
    if (confirm('Are you sure you want to delete this crew change?')) {
      const index = this.crew.crewChanges.findIndex(c => c.id === this.crewChange.id);
      this.crew.crewChanges.splice(index, 1);
      this.update();
    }
  }

  vesselFormatter(vessel: Vessel) {
    if (vessel) {
      return vessel.name!.name;
    }
    return '';
  }

  async suggestUsername() {
    const result = await this._crewService.addCrewSsoUsername({ crewId: this.crew.id, expectedVersion: this.expectedVersion });
    this.crew.ssoUsername = result.assignedSsoUsername;
    this._dialog.open(TemporaryPasswordDialogComponent, {
      data: {
        username: result.assignedSsoUsername,
        password: result.temporarySsoPassword
      },
      width: '450px',
      disableClose: true
    });
  }

  async addNewCrewChange() {
    const request: AddNewCrewChangeRequest = {
      crewId: this.crew.id,
      rank: this.crewChange.rank,
      vesselImo: this.crewChange.vesselImo,
      vesselName: this.crewChange.vesselName,
      voyageNumber: this.selectedVoyage.shipVoyageNo,
      expectedVersion: this.expectedVersion
    };
    const response = await this._crewService.addNewCrewChange(request);
    this._router.navigate(['vessels/crew-change/crew-change-data-entry', response.crewId, response.crewChangeId]).then(() => {
    });
  }

  save() {
    this.crewChange.voyageNumber = this.selectedVoyage.shipVoyageNo;
    this.update();
  }

  update() {
    if (!this.crew.ssoUsername && this.crewChange.rank === 'Master') {
      this._pgMessagesService.publishMessage({
        message: `${this.crew.firstName} ${this.crew.lastName} require sso username.`,
        topic: 'Crew change',
        level: MessageLevel.Error,
      });
      return;
    }
    const request: UpdateCrewChangePlanRequest = {
      crewId: this.crew.id,
      crewChangeId: this.crewChangeId || '',
      expectedVersion: this.expectedVersion,
      incomingFlight: this.crewChange.item[0],
      onBoard: this.crewChange.item[1],
      offBoard: this.crewChange.item[2],
      outgoingFlight: this.crewChange.item[3]
    };
    this._crewService.updateCrewChangePlan(request)
      .then(() => {
        this._pgMessagesService.publishMessage({
          message: `${this.crew.firstName} ${this.crew.lastName}`,
          topic: 'Crew change added',
          level: MessageLevel.Info,
        });
        this._router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this._router
              .navigate([
                'vessels/crew-change/crew-change-data-entry',
                this.crew.id,
                this.crewChange.id
              ])
              .then();
          });
      });
  }

  updateContract(contract: CrewContract) {
    const request: UpdateCrewChangeContractRequest = {
      crewId: this.crew.id,
      crewChangeId: this.crewChangeId || '',
      expectedVersion: this.expectedVersion,
      contract: contract,
    };
    this._crewService.updateCrewChangeContract(request)
      .then(() => {
        this._pgMessagesService.publishMessage({
          message: `${this.crew.firstName} ${this.crew.lastName} Contract`,
          topic: 'Crew Contract added',
          level: MessageLevel.Info,
        });
        this._router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this._router
              .navigate([
                'vessels/crew-change/crew-change-data-entry',
                this.crew.id,
                this.crewChange.id
              ])
              .then();
          });
      });
  }

  hasRole(roleName: string) {
    return this._keycloakService.hasRole(roleName);
  }

  extractDateTime(crewChangeItem?: CrewChangeItem) {
    if (crewChangeItem && crewChangeItem.time && crewChangeItem.place) {
      const zoneId = this.organisations.find(o => o.name === crewChangeItem.place)?.timezone || '';
      return DateTimeHelpers.getDateFromTimestamp(zoneId, crewChangeItem.time);
    }
    return '';
  }

  crewChangeFindPlan(ch: CrewChange, plan: string) {
    return ch.item?.find(c => c.plan === plan);
  }

  duration(ch: CrewChange) {
    const on = this.crewChangeFindPlan(ch, 'On Board');
    const off = this.crewChangeFindPlan(ch, 'Off Board');
    if (on && off) {
      return moment(off?.time)
        .diff(moment(on?.time), 'months', true)
        .toFixed(2);
    } else {
      return '-';
    }
  }

  newContractExtension() {
    if (this.crewChange.contract) {
      const extension: CrewContract = CrewContract.create();
      this.crewChange.contract.extensions.push(extension);
    }
  }
}